import type { AppSummary } from "@compath/renderer";
import { metaFor } from "../../models/catalog";

interface Props {
  apps: AppSummary[];
  query: string;
  onQuery: (q: string) => void;
  selectedId: string | null;
  admin: boolean;
  onSelect: (id: string) => void;
  onOpen: (app: AppSummary) => void;
  onLink: () => void;
}

export function Sidebar({ apps, query, onQuery, selectedId, admin, onSelect, onOpen, onLink }: Props) {
  const q = query.trim().toLowerCase();
  const match = (a: AppSummary) =>
    !q || a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q);
  const installed = apps.filter((a) => a.entitled && match(a));
  const explore = apps.filter((a) => !a.entitled && match(a));

  return (
    <aside className="sidebar">
      <div className="side-head">
        <span className="side-title">앱</span>
        {admin && (
          <button className="link-btn" title="앱 링크 (관리자)" onClick={onLink}>＋ 링크</button>
        )}
      </div>
      <label className="search">
        🔍
        <input placeholder="앱 검색" value={query} onChange={(e) => onQuery(e.target.value)} />
      </label>

      <div className="side-section">설치됨 · {installed.length}</div>
      {installed.map((a) => (
        <Row key={a.id} app={a} selected={a.id === selectedId} onSelect={onSelect}>
          <button className="pill open" onClick={(e) => { e.stopPropagation(); onOpen(a); }}>열기</button>
        </Row>
      ))}
      {installed.length === 0 && <Empty text="설치된 앱이 없습니다" />}

      <div className="side-section">탐색 · {explore.length}</div>
      {explore.map((a) => {
        const paid = !!(a.purchaseUrl || metaFor(a.id).priceLabel);
        return (
          <Row key={a.id} app={a} selected={a.id === selectedId} onSelect={onSelect}>
            <span className={`pill ${paid ? "buy-ghost" : "install"}`}>{paid ? "구매" : "설치"}</span>
          </Row>
        );
      })}
      {explore.length === 0 && <Empty text="탐색할 앱이 없습니다" />}
    </aside>
  );
}

function Row({
  app, selected, onSelect, children,
}: {
  app: AppSummary; selected: boolean; onSelect: (id: string) => void; children: React.ReactNode;
}) {
  const m = metaFor(app.id);
  // A div (not a button) so the row can hold an interactive child (the 열기/구매/설치 pill button);
  // a <button> inside a <button> is invalid HTML and trips React's nesting warning.
  return (
    <div
      className={`app-row ${selected ? "selected" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(app.id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(app.id); } }}
    >
      <span className="app-tile" style={{ background: m.tint }}>{m.emoji}</span>
      <span className="app-mid">
        <span className="app-name">{app.name}</span>
        <span className="app-desc">{m.summary}</span>
      </span>
      {children}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div style={{ padding: "6px 8px", fontSize: 13, color: "var(--ink3)" }}>{text}</div>;
}
